import { chosenOption, type ChatMessage } from '@mg/shared';

/**
 * What a user's message should READ as in the transcript.
 *
 * Clicking an option button sends that option's `value`, not its label, and the value is often not
 * language: `ALL_SOCIAL`, or a bare account UUID. The user clicked a button that said "All
 * connected accounts" and then watched their own message come back as
 * `0256e96c-722c-4b88-bb00-d39f8d51bfcf`.
 *
 * The value still has to be what is SENT — the model needs the account id, and the system prompt's
 * flows are written against those exact values — so only the rendering changes.
 *
 * The label is recovered from the question rather than stored alongside the answer: the assistant
 * message that offered the buttons is already persisted with its full option list, so the pairing
 * is right there in the history. That needs no new column, no change to what the API accepts, and
 * it repairs conversations that were held BEFORE this existed — a stored transcript full of raw
 * UUIDs starts reading properly, which a new field on new messages could never do.
 *
 * `previous` is the question this message may be answering, which the caller finds with
 * `questionFor` (liveChoice.ts): the nearest reply before it that is not a failed turn. Buttons are
 * only ever live on that one message, so matching against it alone is exact rather than a search
 * for the nearest plausible question. It is usually the message directly before — but not after a
 * failed turn, where an answer clicked again sits after the apology. Free text typed into
 * "Something else…" matches no option and is shown as typed.
 */
export function userMessageText(message: ChatMessage, previous?: ChatMessage | null): string {
  if (message.role !== 'user') return message.content;

  // The SAME matcher the server uses to replay this answer to the model, so the bubble and the
  // model can never disagree about which option was clicked.
  const picked = chosenOption(message.content, previous);
  // A label is only an improvement when it says something; an empty one would blank the bubble.
  return picked?.label.trim() ? picked.label : message.content;
}
